import type { LucideIcon } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './Chip.module.css';

export type ChipOption<V extends string = string> = {
  value: V;
  label: ReactNode;
  icon?: LucideIcon;
  /** Leading visual such as an emoji or an <Avatar size="sm">. Decorative. */
  leading?: ReactNode;
  disabled?: boolean;
};

type Common<V extends string> = {
  options: ChipOption<V>[];
  /** Accessible group label, e.g. "Occasion kind". */
  label: string;
  /** Horizontally scrollable single row (mobile pattern) instead of wrapping. */
  scroll?: boolean;
  className?: string;
};

export type ChipGroupProps<V extends string = string> =
  | (Common<V> & { multiple?: false; value: V | null; onValueChange: (value: V) => void })
  | (Common<V> & { multiple: true; value: V[]; onValueChange: (value: V[]) => void });

/**
 * Selectable chips. Single-select behaves like a radio group (arrow keys move and select);
 * multi-select is a set of toggle buttons (aria-pressed).
 */
export function ChipGroup<V extends string>(props: ChipGroupProps<V>) {
  const { options, label, scroll, className } = props;
  const isSelected = (v: V) => (props.multiple ? props.value.includes(v) : props.value === v);

  const select = (v: V) => {
    if (props.multiple) {
      props.onValueChange(
        props.value.includes(v) ? props.value.filter((x) => x !== v) : [...props.value, v],
      );
    } else {
      props.onValueChange(v);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (props.multiple) return;
    const dir =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? -1
          : 0;
    if (!dir) return;
    e.preventDefault();
    const enabled = options.filter((o) => !o.disabled);
    const current = enabled.findIndex((o) => o.value === options[index]?.value);
    const next = enabled[(current + dir + enabled.length) % enabled.length];
    if (!next) return;
    select(next.value);
    const buttons = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[options.indexOf(next)]?.focus();
  };

  const selectedIndex = props.multiple ? -1 : options.findIndex((o) => o.value === props.value);

  return (
    <div
      role={props.multiple ? 'group' : 'radiogroup'}
      aria-label={label}
      className={cx(s.group, className)}
      data-scroll={scroll || undefined}
    >
      {options.map((o, i) => {
        const selected = isSelected(o.value);
        const tabbable = props.multiple || selected || (selectedIndex === -1 && i === 0);
        return (
          <button
            key={o.value}
            type="button"
            className={s.chip}
            data-selected={selected || undefined}
            disabled={o.disabled}
            {...(props.multiple
              ? { 'aria-pressed': selected }
              : { role: 'radio', 'aria-checked': selected })}
            tabIndex={tabbable ? 0 : -1}
            onClick={() => select(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {o.leading ? (
              <span className={s.leading} aria-hidden="true">
                {o.leading}
              </span>
            ) : null}
            {o.icon ? <Icon icon={o.icon} size="sm" /> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
